import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { patientService } from '../services/patientService';
import { consultationService } from '../../../services/consultationService';
import { appointmentService } from '../../../services/appointmentService';
import { surgeryService, type SurgicalProcedure, PROCEDURE_TYPE_LABELS, STATUS_BADGE, STATUS_LABELS } from '../../surgery/surgeryService';
import { preAuthorizationService, type PreAuthorization } from '../../../services/preAuthorizationService';
import type { MedicalConsultation } from '../../../types/api';
import type { ClinicalSummary } from '../../../services/patientService';
import ClinicalSummaryBand from '../components/ClinicalSummary';
import EmptyState from '../../../components/ui/EmptyState';
import Spinner from '../../../components/ui/Spinner';
import Timeline from '../../../components/ui/Timeline';
import { useDoctorSpecialty } from '../../auth/hooks/useDoctorSpecialty';
import GynecolologicalHistoryForm from '../components/GynecolologicalHistoryForm';
import PersonalPathologicalHistory from '../components/PersonalPathologicalHistory';
import FamilyHistory from '../components/FamilyHistory';
import HistoryCategoryCard from '../components/HistoryCategoryCard';
import PatientDocumentsTab from '../components/PatientDocumentsTab';
import PatientInsurancesTab from '../components/PatientInsurancesTab';
import { PRE_AUTH_STATUS_BADGE, PRE_AUTH_STATUS_LABELS } from '../../../domain/statuses';
import { formatDate, parseApiDate, formatGender, genderIcon } from '../../../lib/format';
import { medicationCountLabel } from '../utils/medicationCountLabel';

interface PatientDetail {
  id: number;
  medical_record_number: string;
  first_name: string;
  last_name: string;
  identification: string;
  birth_date: string;
  gender: boolean | null;
  phone: string;
  mobile: string;
  email: string;
  address: string;
  status: number;
  nationality: { id: number; name: string };
  identification_type: { id: number; name: string };
  province: { id: number; name: string };
  municipality: { id: number; name: string };
  municipal_district: { id: number; name: string };
  insurances: Array<{
    id: number;
    policy_number: string;
    nss: string;
    card_number: string;
    insuranceCompanyPlan?: {
      company?: { id: number; name: string };
      plan?: { id: number; name: string };
    };
  }>;
  allergies: Array<{
    id: number;
    name: string;
    category: { name: string };
    notes: string;
  }>;
  contacts: Array<{
    id: number;
    name: string;
    phone: string;
    mobile: string;
    email: string;
    relationship: string;
    is_primary: boolean;
  }>;
  medical_history?: Array<{
    id: number;
    description: string;
    onset_date: string | null;
    end_date: string | null;
    is_active: boolean;
    notes: string | null;
    history_type: {
      id: number;
      name: string;
      code: string;
      category: string;
    };
  }>;
}

const MORE_TAB_KEYS = ['contacts', 'pre-auths', 'documents'];

// "Personales" types not already covered by the dedicated PersonalPathologicalHistory
// card or by other patient tabs (Cirugías, Alergias).
const OTHER_PERSONAL_TYPE_CODES = ['TRAUM', 'TOX', 'TRANS', 'HOSP', 'MED'];

const PatientDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('info');
  const [historySubTab, setHistorySubTab] = useState<
    'pathological' | 'family' | 'gynecological' | 'other' | 'vaccination' | 'obstetric'
  >('pathological');
  const { isGynecologist } = useDoctorSpecialty();

  // "Más" tab dropdown state
  const [moreOpen, setMoreOpen] = useState(false);
  const [summary, setSummary] = useState<ClinicalSummary | null>(null);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const moreDropdownRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!moreOpen) return;
    const closeOnOutsideClick = (e: MouseEvent) => {
      if (moreDropdownRef.current && !moreDropdownRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [moreOpen]);

  // Consultation history state
  const [consultations, setConsultations] = useState<MedicalConsultation[]>([]);
  const [loadingConsultations, setLoadingConsultations] = useState(false);

  // Surgery history state
  const [surgeries, setSurgeries] = useState<SurgicalProcedure[]>([]);
  const [loadingSurgeries, setLoadingSurgeries] = useState(false);

  // Appointments history state
  const [appointments, setAppointments] = useState<any[]>([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);

  // Pre-authorizations state
  const [preAuths, setPreAuths] = useState<PreAuthorization[]>([]);
  const [loadingPreAuths, setLoadingPreAuths] = useState(false);

  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await patientService.getWithHistory(parseInt(id));
        setPatient(data);
      } catch (err) {
        console.error('Error fetching patient:', err);
        setError('Error loading patient');
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  // Loaded separately from /history: the summary is a much smaller query,
  // so the clinical band paints without waiting for ten consultations and
  // their relations. A failure here must not take down the chart.
  useEffect(() => {
    if (!id) return;
    setLoadingSummary(true);
    patientService.getClinicalSummary(parseInt(id))
      .then(setSummary)
      .catch(err => console.error('Error fetching clinical summary:', err))
      .finally(() => setLoadingSummary(false));
  }, [id]);

  useEffect(() => {
    if (activeTab === 'consultations' && id && consultations.length === 0) {
      setLoadingConsultations(true);
      consultationService.getPatientHistory(parseInt(id))
        .then(setConsultations)
        .catch(() => { void 0; })
        .finally(() => setLoadingConsultations(false));
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === 'surgeries' && id && surgeries.length === 0) {
      setLoadingSurgeries(true);
      surgeryService.getAll({ patient_id: parseInt(id), per_page: 100 })
        .then(res => setSurgeries(res.data || []))
        .catch(() => { void 0; })
        .finally(() => setLoadingSurgeries(false));
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === 'appointments' && id && appointments.length === 0) {
      setLoadingAppointments(true);
      appointmentService.getAll({ patient_id: parseInt(id), perPage: 100 })
        .then(res => setAppointments(res.data || []))
        .catch(() => { void 0; })
        .finally(() => setLoadingAppointments(false));
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === 'pre-auths' && id && preAuths.length === 0) {
      setLoadingPreAuths(true);
      preAuthorizationService.getByPatient(parseInt(id))
        .then(data => setPreAuths(data))
        .catch(() => { void 0; })
        .finally(() => setLoadingPreAuths(false));
    }
  }, [activeTab, id]);


  if (loading) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <Spinner variant="full-page" text="Cargando..." size="lg" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="alert alert-danger">
              {error || 'Paciente no encontrado'}
              <Link to="/patients" className="btn btn-outline-secondary btn-sm float-end">
                Volver
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const calcAge = (birthDate: string) => {
    if (!birthDate) return null;
    const [year, month, day] = birthDate.slice(0, 10).split('-').map(Number);
    if (!year || !month || !day) return null;
    const diff = Date.now() - new Date(year, month - 1, day).getTime();
    return Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
  };
  const age = patient.birth_date ? calcAge(patient.birth_date) : null;
  const hasActiveAllergies = patient.allergies && patient.allergies.length > 0;
  const isMale = patient.gender === true;

  return (
    <>
    <div className="container-fluid">
      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">
                <i className="fa fa-user"></i> Detalles del Paciente
              </h3>
              <div className="card-tools">
                <Link to={`/patients/${patient.id}/edit`} className="btn btn-primary btn-sm">
                  <i className="fa fa-edit"></i> Editar
                </Link>
                <Link to="/patients" className="btn btn-outline-secondary btn-sm">
                  <i className="fa fa-arrow-left"></i> Volver
                </Link>
              </div>
            </div>

            <div className="card-body">
              {/* Allergy Alert Banner */}
              {hasActiveAllergies && (
                <div className="alert alert-danger d-flex align-items-center mb-3" style={{ borderLeft: '5px solid var(--ec-danger)' }}>
                  <i className="fa fa-exclamation-triangle fa-lg me-3"></i>
                  <div>
                    <strong>ALERGIAS REGISTRADAS: </strong>
                    {patient.allergies.map(a => a.name).join(', ')}
                  </div>
                </div>
              )}

              {/* Patient Header */}
              <div className="row" style={{ marginBottom: '20px' }}>
                <div className="col-md-3">
                  <div className="text-center">
                    <div style={{
                      width: 80, height: 80, borderRadius: '50%',
                      background: genderIcon(patient.gender).background,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 12px',
                    }}>
                      <i className={`fa ${genderIcon(patient.gender).className} fa-2x`}
                        style={{ color: genderIcon(patient.gender).color }}></i>
                    </div>
                    <h4 className="mb-1">{patient.first_name} {patient.last_name}</h4>
                    {age !== null && (
                      <div className="text-muted" style={{ fontSize: 13, marginBottom: 6 }}>
                        {age} años — {formatGender(patient.gender)}
                      </div>
                    )}
                    <span className={`badge ${patient.status === 1 ? 'bg-success' : 'bg-secondary'}`}>
                      {patient.status === 1 ? 'Activo' : 'Inactivo'}
                    </span>
                    <div className="mt-2 d-flex justify-content-center gap-2">
                      <Link to={`/consultations/new?patient_id=${patient.id}`} className="btn btn-sm btn-primary" title="Nueva consulta">
                        <i className="fa fa-stethoscope"></i>
                      </Link>
                      <Link to={`/appointments/new?patient_id=${patient.id}`} className="btn btn-sm btn-outline-secondary" title="Nueva cita">
                        <i className="fa fa-calendar-plus"></i>
                      </Link>
                    </div>
                  </div>
                </div>
                <div className="col-md-9">
                  <div className="row">
                    <div className="col-md-4">
                      <strong>Expediente:</strong> {patient.medical_record_number}
                    </div>
                    <div className="col-md-4">
                      <strong>Identificación:</strong> {patient.identification}
                    </div>
                    <div className="col-md-4">
                      <strong>Tipo ID:</strong> {patient.identification_type?.name || '-'}
                    </div>
                  </div>
                  <div className="row" style={{ marginTop: '10px' }}>
                    <div className="col-md-4">
                      <strong>Género:</strong> {formatGender(patient.gender)}
                    </div>
                    <div className="col-md-4">
                      <strong>Fecha Nac.:</strong> {formatDate(patient.birth_date)}
                    </div>
                    <div className="col-md-4">
                      <strong>Nacionalidad:</strong> {patient.nationality?.name || '-'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Clinical summary — answers "what should I know about this
                  patient right now?" before the tabs are touched */}
              <ClinicalSummaryBand summary={summary} loading={loadingSummary} />

              {/* Tabs */}
              <div>
                <ul className="nav nav-tabs ec-tabs" role="tablist">
                  {([
                    { key: 'info', icon: 'fa-info-circle', label: 'Información', count: 0 },
                    { key: 'history', icon: 'fa-heartbeat', label: 'Historial', count: 0 },
                    { key: 'consultations', icon: 'fa-stethoscope', label: 'Clínica', count: consultations.length },
                    { key: 'allergies', icon: 'fa-exclamation-triangle', label: 'Alergias', count: patient.allergies?.length ?? 0, danger: true },
                    { key: 'appointments', icon: 'fa-calendar-check', label: 'Citas', count: appointments.length },
                    { key: 'surgeries', icon: 'fa-medkit', label: 'Cirugías', count: surgeries.length },
                    { key: 'insurances', icon: 'fa-shield', label: 'Seguros', count: patient.insurances?.length ?? 0 },
                  ] as Array<{ key: string; icon: string; label: string; count: number; danger?: boolean }>).map(tab => (
                    <li className="nav-item" key={tab.key} role="presentation">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={activeTab === tab.key}
                        aria-controls={tab.key}
                        className={`nav-link${activeTab === tab.key ? ' active' : ''}`}
                        onClick={() => { setActiveTab(tab.key); setMoreOpen(false); }}
                      >
                        <i className={`fa ${tab.icon}`}></i> {tab.label}
                        {tab.count > 0 && (
                          <span className={`badge rounded-pill ec-tab-count${tab.danger ? ' ec-tab-count-danger' : ''}`}>{tab.count}</span>
                        )}
                      </button>
                    </li>
                  ))}
                  <li className="nav-item dropdown" ref={moreDropdownRef}>
                    <button
                      type="button"
                      className={`nav-link dropdown-toggle${MORE_TAB_KEYS.includes(activeTab) ? ' active' : ''}`}
                      aria-expanded={moreOpen}
                      aria-haspopup="true"
                      onClick={() => setMoreOpen(open => !open)}
                    >
                      <i className="fa fa-ellipsis-h"></i> Más
                    </button>
                    <ul className={`dropdown-menu${moreOpen ? ' show' : ''}`}>
                      <li>
                        <button
                          type="button"
                          className={`dropdown-item${activeTab === 'contacts' ? ' active' : ''}`}
                          onClick={() => { setActiveTab('contacts'); setMoreOpen(false); }}
                        >
                          <i className="fa fa-address-book"></i> Contactos
                          {(patient.contacts?.length ?? 0) > 0 && (
                            <span className="badge rounded-pill ec-tab-count ms-auto">{patient.contacts?.length}</span>
                          )}
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`dropdown-item${activeTab === 'pre-auths' ? ' active' : ''}`}
                          onClick={() => { setActiveTab('pre-auths'); setMoreOpen(false); }}
                        >
                          <i className="fa fa-square-check"></i> Pre-Autorizaciones
                          {preAuths.length > 0 && (
                            <span className="badge rounded-pill ec-tab-count ms-auto">{preAuths.length}</span>
                          )}
                        </button>
                      </li>
                      <li>
                        <button
                          type="button"
                          className={`dropdown-item${activeTab === 'documents' ? ' active' : ''}`}
                          onClick={() => { setActiveTab('documents'); setMoreOpen(false); }}
                        >
                          <i className="fa fa-file-pdf"></i> Documentos
                        </button>
                      </li>
                    </ul>
                  </li>
                </ul>

                <div className="tab-content">
                  {/* Info Tab */}
                  <div className={`tab-pane ${activeTab === 'info' ? 'active' : ''}`} id="info">
                    <div className="row">
                      <div className="col-md-6">
                        <h4>Información de Contacto</h4>
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td><strong>Teléfono:</strong></td>
                              <td>{patient.phone || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>Celular:</strong></td>
                              <td>{patient.mobile || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>Email:</strong></td>
                              <td>{patient.email || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>Dirección:</strong></td>
                              <td>{patient.address || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div className="col-md-6">
                        <h4>Ubicación</h4>
                        <table className="table table-borderless">
                          <tbody>
                            <tr>
                              <td><strong>Provincia:</strong></td>
                              <td>{patient.province?.name || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>Municipio:</strong></td>
                              <td>{patient.municipality?.name || '-'}</td>
                            </tr>
                            <tr>
                              <td><strong>Distrito:</strong></td>
                              <td>{patient.municipal_district?.name || '-'}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  {/* Medical History Tab */}
                  <div className={`tab-pane ${activeTab === 'history' ? 'active' : ''}`} id="history">
                    <ul className="nav nav-pills ec-subtabs mb-3" role="tablist">
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link${historySubTab === 'pathological' ? ' active' : ''}`}
                          onClick={() => setHistorySubTab('pathological')}
                        >
                          <i className="fa fa-file-medical"></i> Patológicos Personales
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link${historySubTab === 'family' ? ' active' : ''}`}
                          onClick={() => setHistorySubTab('family')}
                        >
                          <i className="fa fa-users"></i> Familiares
                        </button>
                      </li>
                      {isGynecologist && (
                        <li className="nav-item">
                          <button
                            type="button"
                            className={`nav-link${historySubTab === 'gynecological' ? ' active' : ''}`}
                            onClick={() => setHistorySubTab('gynecological')}
                          >
                            <i className="fa fa-venus"></i> Ginecológicos
                          </button>
                        </li>
                      )}
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link${historySubTab === 'other' ? ' active' : ''}`}
                          onClick={() => setHistorySubTab('other')}
                        >
                          <i className="fa fa-notes-medical"></i> Otros Antecedentes
                        </button>
                      </li>
                      <li className="nav-item">
                        <button
                          type="button"
                          className={`nav-link${historySubTab === 'vaccination' ? ' active' : ''}`}
                          onClick={() => setHistorySubTab('vaccination')}
                        >
                          <i className="fa fa-shield"></i> Vacunas
                        </button>
                      </li>
                      {!isMale && (
                        <li className="nav-item">
                          <button
                            type="button"
                            className={`nav-link${historySubTab === 'obstetric' ? ' active' : ''}`}
                            onClick={() => setHistorySubTab('obstetric')}
                          >
                            <i className="fa fa-venus"></i> Obstétricos
                          </button>
                        </li>
                      )}
                    </ul>

                    {historySubTab === 'pathological' && (
                      <PersonalPathologicalHistory patientId={patient.id} />
                    )}
                    {historySubTab === 'family' && (
                      <FamilyHistory patientId={patient.id} />
                    )}
                    {historySubTab === 'gynecological' && isGynecologist && (
                      <GynecolologicalHistoryForm patientId={patient.id} />
                    )}
                    {historySubTab === 'other' && (
                      <HistoryCategoryCard
                        patientId={patient.id}
                        category="personal"
                        includeTypeCodes={OTHER_PERSONAL_TYPE_CODES}
                        title="Otros Antecedentes"
                        icon="fa-notes-medical"
                      />
                    )}
                    {historySubTab === 'vaccination' && (
                      <HistoryCategoryCard
                        patientId={patient.id}
                        category="vaccination"
                        title="Vacunas"
                        icon="fa-shield"
                      />
                    )}
                    {historySubTab === 'obstetric' && !isMale && (
                      <HistoryCategoryCard
                        patientId={patient.id}
                        category="obstetric"
                        title="Antecedentes Obstétricos"
                        icon="fa-venus"
                      />
                    )}
                  </div>

                  {/* Insurances Tab */}
                  <div className={`tab-pane ${activeTab === 'insurances' ? 'active' : ''}`} id="insurances">
                    <PatientInsurancesTab patientId={patient.id} />
                  </div>

                  {/* Allergies Tab */}
                  <div className={`tab-pane ${activeTab === 'allergies' ? 'active' : ''}`} id="allergies">
                    {patient.allergies && patient.allergies.length > 0 ? (
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Alergia</th>
                            <th>Categoría</th>
                            <th>Notas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {patient.allergies.map((allergy) => (
                            <tr key={allergy.id}>
                              <td>{allergy.name}</td>
                              <td>{allergy.category?.name || '-'}</td>
                              <td>{allergy.notes || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <EmptyState icon="fa-exclamation-triangle" title="No tiene alergias registradas" className="py-4" />
                    )}
                  </div>

                  {/* Consultations Tab */}
                  <div className={`tab-pane ${activeTab === 'consultations' ? 'active' : ''}`} id="consultations">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Historia Clínica</h5>
                      <Link
                        to={`/consultations/new?patient_id=${patient.id}`}
                        className="btn btn-primary btn-sm"
                      >
                        <i className="fa fa-plus me-1"></i> Nueva Consulta
                      </Link>
                    </div>

                    {loadingConsultations ? (
                      <Spinner variant="full-page" text="Cargando consultas..." size="md" />
                    ) : consultations.length === 0 ? (
                      <EmptyState icon="fa-file-medical" title="No hay consultas registradas" description="Este paciente no tiene consultas previas." action={{ label: 'Registrar primera consulta', to: '/consultations/new?patient_id=' + patient.id, icon: 'fa-plus' }} className="py-4" />
                    ) : (
                      <Timeline
                        items={consultations.map(c => ({ ...c, date: c.consultation_date }))}
                        variant="primary"
                        renderItem={(c) => (
                          <>
                            <div className="d-flex justify-content-between align-items-start">
                              <div>
                                <strong style={{ fontSize: 14 }}>
                                  {c.chief_complaint}
                                </strong>
                                {c.doctor && (
                                  <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                                    <i className="fa fa-user-md me-1"></i>
                                    Dr. {c.doctor.first_name} {c.doctor.last_name}
                                    {c.doctor.specialty && ` — ${c.doctor.specialty.name}`}
                                  </div>
                                )}
                              </div>
                              <Link
                                to={`/consultations/${c.id}`}
                                className="btn btn-outline-primary btn-sm"
                                style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
                              >
                                <i className="fa fa-eye me-1"></i> Ver
                              </Link>
                            </div>

                            {/* Diagnoses */}
                            {(c.diagnoses || []).length > 0 && (
                              <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {(c.diagnoses as any[]).slice(0, 3).map((d: any) => (
                                  <span key={d.id} style={{
                                    background: (d.type || d.diagnosis_type) === 'principal' ? '#dbeafe' : '#f3f4f6',
                                    color: (d.type || d.diagnosis_type) === 'principal' ? '#1d4ed8' : '#6b7280',
                                    padding: '2px 8px',
                                    borderRadius: 20,
                                    fontSize: 11,
                                    fontWeight: 500,
                                  }}>
                                    {d.diagnosis?.code} — {d.diagnosis?.description}
                                  </span>
                                ))}
                                {(c.diagnoses as any[]).length > 3 && (
                                  <span style={{ fontSize: 11, color: '#9ca3af', padding: '2px 4px' }}>
                                    +{(c.diagnoses as any[]).length - 3} más
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Vital signs quick summary */}
                            {c.vital_signs && (
                              <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                                {c.vital_signs.systolic_pressure != null && (
                                  <span style={{ fontSize: 11, color: '#64748b' }}>
                                    <i className="fa fa-heart me-1" style={{ color: '#ef4444' }}></i>
                                    {c.vital_signs.systolic_pressure}/{c.vital_signs.diastolic_pressure} mmHg
                                  </span>
                                )}
                                {c.vital_signs.temperature != null && (
                                  <span style={{ fontSize: 11, color: '#64748b' }}>
                                    <i className="fa fa-thermometer me-1" style={{ color: '#f59e0b' }}></i>
                                    {c.vital_signs.temperature}°C
                                  </span>
                                )}
                                {c.vital_signs.weight != null && (
                                  <span style={{ fontSize: 11, color: '#64748b' }}>
                                    <i className="fa fa-balance-scale me-1"></i>
                                    {c.vital_signs.weight} kg
                                  </span>
                                )}
                                {c.vital_signs.oxygen_saturation != null && (
                                  <span style={{ fontSize: 11, color: '#64748b' }}>
                                    <i className="fa fa-tint me-1" style={{ color: '#3b82f6' }}></i>
                                    SpO2 {c.vital_signs.oxygen_saturation}%
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Prescriptions & lab count */}
                            <div style={{ marginTop: 6, display: 'flex', gap: 12 }}>
                              {medicationCountLabel(c.prescriptions) && (
                                <span style={{ fontSize: 11, color: '#7c3aed' }}>
                                  <i className="fa fa-file-lines me-1"></i>
                                  {medicationCountLabel(c.prescriptions)}
                                </span>
                              )}
                              {((c as any).lab_orders || c.labOrders || []).length > 0 && (
                                <span style={{ fontSize: 11, color: '#059669' }}>
                                  <i className="fa fa-flask me-1"></i>
                                  {((c as any).lab_orders || c.labOrders as any[]).length} lab
                                </span>
                              )}
                              {c.follow_up_date && (
                                <span style={{ fontSize: 11, color: '#0EA5A4' }}>
                                  <i className="fa fa-calendar me-1"></i>
                                  Control: {formatDate(c.follow_up_date)}
                                </span>
                              )}
                            </div>
                          </>
                        )}
                      />
                    )}
                  </div>

                  {/* Appointments Tab */}
                  <div className={`tab-pane ${activeTab === 'appointments' ? 'active' : ''}`} id="appointments">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Historial de Citas</h5>
                      <Link to={`/appointments/new?patient_id=${patient.id}`} className="btn btn-primary btn-sm">
                        <i className="fa fa-plus me-1"></i> Nueva Cita
                      </Link>
                    </div>

                    {loadingAppointments ? (
                      <Spinner variant="full-page" text="Cargando citas..." size="md" />
                    ) : appointments.length === 0 ? (
                      <EmptyState icon="fa-calendar" title="No hay citas registradas" description="Este paciente no tiene citas programadas." action={{ label: 'Agendar cita', to: '/appointments/new?patient_id=' + patient.id, icon: 'fa-plus' }} className="py-4" />
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead className="thead-light">
                            <tr>
                              <th>Fecha</th>
                              <th>Hora</th>
                              <th>Doctor</th>
                              <th>Tipo</th>
                              <th>Estado</th>
                              <th className="text-end">Acciones</th>
                            </tr>
                          </thead>
                          <tbody>
                            {appointments.map(appt => {
                              // scheduled_date is a DATE column: parsing it natively
                              // lands a day early, which both shows the wrong day and
                              // can mark today's appointment as already past.
                              const apptDate = parseApiDate(appt.appointment_date || appt.scheduled_date);
                              const isPast = apptDate ? apptDate < new Date() : false;
                              return (
                                <tr key={appt.id}>
                                  <td>
                                    <span style={{ fontWeight: isPast ? 400 : 600 }}>
                                      {apptDate
                                        ? apptDate.toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' })
                                        : '-'}
                                    </span>
                                  </td>
                                  <td>{appt.appointment_time || appt.scheduled_time || '-'}</td>
                                  <td>
                                    {appt.doctor
                                      ? `Dr. ${appt.doctor.first_name} ${appt.doctor.last_name}`
                                      : '-'}
                                  </td>
                                  <td><small className="text-muted">{appt.type_name || appt.type?.name || '-'}</small></td>
                                  <td>
                                    <span className={`custom-badge ${
                                      appt.status_code === 'completed' ? 'status-green' :
                                      appt.status_code === 'cancelled' ? 'status-pink' :
                                      appt.status_code === 'confirmed' ? 'status-blue' :
                                      'status-orange'
                                    }`}>
                                      {appt.status_name || appt.status?.name || appt.status_code || '-'}
                                    </span>
                                  </td>
                                  <td className="text-end">
                                    <Link to={`/appointments/${appt.id}`} className="btn btn-sm btn-outline-primary">
                                      <i className="fa fa-eye"></i>
                                    </Link>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Surgeries Tab */}
                  <div className={`tab-pane ${activeTab === 'surgeries' ? 'active' : ''}`} id="surgeries">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="mb-0">Historial Quirúrgico</h5>
                      <Link
                        to={`/surgery/new?patient_id=${patient.id}`}
                        className="btn btn-primary btn-sm"
                      >
                        <i className="fa fa-plus me-1"></i> Nuevo Procedimiento
                      </Link>
                    </div>

                    {loadingSurgeries ? (
                      <Spinner variant="full-page" text="Cargando cirugías..." size="md" />
                    ) : surgeries.length === 0 ? (
                      <EmptyState icon="fa-kit-medical" title="No hay procedimientos quirúrgicos registrados" action={{ label: 'Registrar primer procedimiento', to: '/surgery/new?patient_id=' + patient.id, icon: 'fa-plus' }} className="py-4" />
                    ) : (
                      <Timeline
                        items={surgeries.map(s => ({ ...s, date: s.procedure_date }))}
                        variant="navy"
                        renderItem={(s) => (
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong style={{ fontSize: 14 }}>{s.procedure_name}</strong>
                              <div className="text-muted" style={{ fontSize: 12, marginTop: 2 }}>
                                <span className="me-2">
                                  <i className="fa fa-tag me-1"></i>
                                  {PROCEDURE_TYPE_LABELS[s.procedure_type] ?? s.procedure_type}
                                </span>
                                {s.doctor && (
                                  <span>
                                    <i className="fa fa-user-md me-1"></i>
                                    Dr. {s.doctor.first_name} {s.doctor.last_name}
                                  </span>
                                )}
                              </div>
                              <div style={{ marginTop: 6 }}>
                                <span className={STATUS_BADGE[s.status] ?? 'custom-badge status-grey'} style={{ fontSize: 11 }}>
                                  {STATUS_LABELS[s.status] ?? s.status}
                                </span>
                                {s.duration_minutes && (
                                  <span className="text-muted ms-2" style={{ fontSize: 11 }}>
                                    <i className="fa fa-clock me-1"></i>{s.duration_minutes} min
                                  </span>
                                )}
                              </div>
                              {s.diagnosis && (
                                <div className="text-muted" style={{ fontSize: 12, marginTop: 6 }}>
                                  <i className="fa fa-stethoscope me-1"></i>{s.diagnosis}
                                </div>
                              )}
                              {s.complications && (
                                <div style={{ marginTop: 4, fontSize: 12, color: '#dc3545' }}>
                                  <i className="fa fa-exclamation-triangle me-1"></i>
                                  Complicaciones: {s.complications}
                                </div>
                              )}
                            </div>
                            <Link
                              to={`/surgery/${s.id}`}
                              className="btn btn-outline-secondary btn-sm"
                              style={{ marginLeft: 8, whiteSpace: 'nowrap' }}
                            >
                              <i className="fa fa-eye me-1"></i> Ver
                            </Link>
                          </div>
                        )}
                      />
                    )}
                  </div>

                  {/* Contacts Tab */}
                  <div className={`tab-pane ${activeTab === 'contacts' ? 'active' : ''}`} id="contacts">
                    {patient.contacts && patient.contacts.length > 0 ? (
                      <table className="table table-striped">
                        <thead>
                          <tr>
                            <th>Nombre</th>
                            <th>Relación</th>
                            <th>Teléfono</th>
                            <th>Celular</th>
                            <th>Email</th>
                            <th>Principal</th>
                          </tr>
                        </thead>
                        <tbody>
                          {patient.contacts.map((contact) => (
                            <tr key={contact.id}>
                              <td>{contact.name}</td>
                              <td>{contact.relationship || '-'}</td>
                              <td>{contact.phone || '-'}</td>
                              <td>{contact.mobile || '-'}</td>
                              <td>{contact.email || '-'}</td>
                              <td>
                                {contact.is_primary && <span className="badge bg-primary">Principal</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <EmptyState icon="fa-phone" title="No tiene contactos de emergencia" className="py-4" />
                    )}
                  </div>

                  {/* Pre-Authorizations Tab */}
                  <div className={`tab-pane ${activeTab === 'pre-auths' ? 'active' : ''}`} id="pre-auths">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">Pre-Autorizaciones ARS</h6>
                      <Link
                        to={`/pre-authorizations/new?patient_id=${id}`}
                        className="btn btn-primary btn-sm"
                      >
                        <i className="fa fa-plus me-1" /> Nueva Pre-Autorización
                      </Link>
                    </div>
                    {loadingPreAuths ? (
                      <div className="text-center py-3"><Spinner size="sm" /></div>
                    ) : preAuths.length === 0 ? (
                      <EmptyState icon="fa-shield" title="Sin pre-autorizaciones registradas" className="py-4" />
                    ) : (
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>Número</th>
                            <th>Aseguradora</th>
                            <th>Tipo</th>
                            <th>Servicio</th>
                            <th>Estado</th>
                            <th>Fecha</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {preAuths.map((p) => {
                            return (
                              <tr key={p.id}>
                                <td><code>{p.authorization_number}</code></td>
                                <td>{p.patientInsurance?.company?.name ?? '-'}</td>
                                <td>{p.authorization_type}</td>
                                <td>{p.service_name}</td>
                                <td><span className={PRE_AUTH_STATUS_BADGE[p.status] || 'badge bg-secondary'}>{PRE_AUTH_STATUS_LABELS[p.status] || 'Desconocido'}</span></td>
                                <td>{p.request_date ? new Date(p.request_date).toLocaleDateString('es-DO') : '-'}</td>
                                <td>
                                  <Link to={`/pre-authorizations/${p.id}`} className="btn btn-sm btn-outline-primary">
                                    <i className="fa fa-eye" />
                                  </Link>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Documents Tab */}
                  <div className={`tab-pane ${activeTab === 'documents' ? 'active' : ''}`} id="documents">
                    {id !== undefined && <PatientDocumentsTab patientId={parseInt(id as string)} />}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PatientDetailPage;
